import * as React from "react";
import { useState } from "react";
import { FieldValues, UseControllerProps, useWatch } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { useDebounce } from "hooks/";
import { useGetManufacturersQuery } from "services/manufacturers";
import { Manufacturer } from "services/types/manufacturers.type";

type ManufacturerFieldProps = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  multiple?: boolean;
  practiceFiltered?: boolean;
  enablePortal?: boolean;
  onOptionSelected?: (option: Manufacturer | null) => void;
  sx?: SxProps;
};

export type RhfManufacturerField<T extends FieldValues> =
  ManufacturerFieldProps & UseControllerProps<T>;

export default function ManufacturerField<T extends FieldValues>({
  control,
  name,
  required,
  placeholder,
  multiple,
  practiceFiltered,
  enablePortal,
  onOptionSelected,
  sx = {},
  label = "Manufacturer"
}: RhfManufacturerField<T>) {
  const option = useWatch({ control, name }) as Manufacturer;

  const [search, setSearch] = useState<string>(option?.name ?? "");

  const { value: dSearch } = useDebounce(search);
  const { data: manufacturers = [], isLoading } = useGetManufacturersQuery(
    { search: dSearch, practice_filtered: practiceFiltered },
    {
      skip: !dSearch && !practiceFiltered
    }
  );

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus
      name={name}
      label={label}
      placeholder={placeholder}
      isLoading={isLoading}
      required={required}
      multiple={multiple}
      noOptionsText={
        !search?.length && !practiceFiltered
          ? `Search ${label?.toLowerCase()} by name...`
          : "No options"
      }
      options={manufacturers}
      getOptionLabel={(option) => {
        return option?.name ?? "";
      }}
      renderOption={(props: object, option: any) => (
        <ListItem {...props}>
          <ListItemText>{option?.name}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          setSearch(option?.name ?? "");
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 250, ...sx }}
    />
  );
}
